import { Button, DrawerProps, IconButton, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Trans } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';

import { Hyperlink } from '@/components/Form/Form.styled';
import { HeaderMobile } from '@/components/Header/HeaderMobile';
import { Icon } from '@/components/Icon/Icon';
import { CaymanCountryLogoWithText } from '@/components/Icon/Logo';
import { ConfirmDrawer } from '@/components/Layout/Drawer/ConfirmDrawer';
import {
  DrawerContentScroller,
  DrawerHeader,
} from '@/components/Layout/Layout.styled';

import { getGDPRConfig } from '@/constants/gdprConfigs';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/selectors';

import { getDataTestId } from '@/utils/browser';
import { isCaymanTenant } from '@/utils/tenant';

import { GDPRDrawerAction, StyledDrawer } from './GDPRDrawer.styled';

type GDPRDrawerProps = {
  onConsent: () => void;
  onNotConsent: () => void;
  onClose: () => void;
} & Omit<DrawerProps, 'onClose'>;

export function GDPRDrawer({
  onConsent,
  onNotConsent,
  onClose,
  ...props
}: GDPRDrawerProps) {
  const {
    value: isConfirming,
    setTrue: showConfirming,
    setFalse: hideConfirming,
  } = useBoolean(false);

  const { t } = useTranslate();
  const { tenant } = useAppSelector(selectConfigsState);
  const config = getGDPRConfig(tenant);

  const handleConsent = () => {
    hideConfirming();
    onConsent();
  };

  const handleNotConsent = () => {
    hideConfirming();
    onNotConsent();
  };

  return (
    <>
      <StyledDrawer {...props} hideBackdrop={false}>
        <HeaderMobile
          leftSlot={
            <IconButton onClick={onClose}>
              <Icon
                name="arrow-left-alt"
                color={(theme: Theme) => theme.palette.grey[700]}
              />
            </IconButton>
          }
          centerSlot={
            <Typography variant="body1Bold">
              {t('termsAndConditions.header')}
            </Typography>
          }
          bgColor={theme => theme.palette.background.paper}
        />
        <DrawerHeader
          sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
        >
          {isCaymanTenant(tenant) && (
            <CaymanCountryLogoWithText
              style={{ width: 150, marginBottom: 12 }}
              {...getDataTestId('cayman-logo')}
            />
          )}
          <Typography variant="h5">
            {t('termsAndConditions.drawer.header')}
          </Typography>
        </DrawerHeader>

        <DrawerContentScroller>
          <Typography variant="body2" component="p">
            <Trans
              i18nKey={t('termsAndConditions.drawer.content')}
              components={{
                a1: (
                  <Hyperlink href={config.privacyPolicyURL} target="_blank" />
                ),
                br: <br />,
              }}
            />
          </Typography>
        </DrawerContentScroller>
        <GDPRDrawerAction align="stretch">
          <Button
            variant="outlined"
            onClick={showConfirming}
            {...getDataTestId(`gdpr-not-consent-button`)}
          >
            {t('termsAndConditions.drawer.button.reject')}
          </Button>
          <Button {...getDataTestId(`gdpr-consent-button`)} onClick={onConsent}>
            {t('termsAndConditions.drawer.button.accept')}
          </Button>
        </GDPRDrawerAction>
      </StyledDrawer>

      {isConfirming && (
        <ConfirmDrawer
          title={t('termsAndConditions.confirmDrawer.title')}
          description={
            <Trans
              i18nKey={t('termsAndConditions.confirmDrawer.description')}
            />
          }
          secondaryAction={handleConsent}
          secondaryText={t('termsAndConditions.confirmDrawer.button.consent')}
          primaryAction={handleNotConsent}
          primaryText={t('termsAndConditions.confirmDrawer.button.confirm')}
          onClose={hideConfirming}
          {...getDataTestId('show-confirm-button-drawer-consent')}
        />
      )}
    </>
  );
}
